import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ClockIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  ArrowRightIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline';

const RotationalShift = () => {
  // State for shifts data
  const [shifts, setShifts] = useState([
    { id: 1, name: 'Morning Shift', start: '09:00 AM', end: '05:00 PM', grace: '15 min' },
    { id: 2, name: 'Evening Shift', start: '05:00 PM', end: '01:00 AM', grace: '15 min' },
    { id: 3, name: 'Night Shift', start: '01:00 AM', end: '09:00 AM', grace: '15 min' },
  ]);

  // State for the new shift form input
  const [newShift, setNewShift] = useState({ name: '', start: '', end: '', grace: '' });

  // Handle input changes for the form
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewShift({ ...newShift, [name]: value });
  };

  // Handle adding a new shift
  const handleAddShift = (e) => {
    e.preventDefault();
    if (newShift.name && newShift.start && newShift.end) {
      setShifts([{ ...newShift, id: Date.now() }, ...shifts]); // Added to the top for visibility
      setNewShift({ name: '', start: '', end: '', grace: '' });
    }
  };

  // Handle deleting a shift
  const handleDeleteShift = (id) => {
    setShifts(shifts.filter(shift => shift.id !== id));
  };

  // Framer motion variants for smooth card entrance
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };
  
  // Custom font style applied to the whole app for required fonts
  const appStyle = {
    fontFamily: 'Roboto, Poppins, sans-serif',
  };

  return (
    // Updated background to plain white and applied custom font style
    <div className="min-h-screen bg-white p-4 sm:p-8 text-gray-800" style={appStyle}> 
      
      {/* Header Section */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-7xl mx-auto mb-8"
      >
        {/* Poppins font style for emphasis on headers */}
        <h1 className="text-3xl sm:text-4xl font-extrabold text-teal-700" style={{ fontFamily: 'Poppins, sans-serif' }}>
          Rotational Shift Management
        </h1>
        <p className="text-md sm:text-xl font-light text-teal-600 mt-2">
          Define and manage work shifts for your employees.
        </p>
      </motion.div>

      {/* Main Content Grid: Responsive Layout (1 column on mobile, 3 on large screens) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
        
        {/* Shift Creation Form */}
        <motion.div
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          className="lg:col-span-1 bg-white p-6 rounded-2xl border border-gray-100 shadow-xl h-full flex flex-col"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>Create New Shift</h3>
          <form onSubmit={handleAddShift} className="space-y-5">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">Shift Name</label>
              <input
                type="text"
                name="name"
                value={newShift.name}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 rounded-lg border border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                placeholder="e.g., General Shift"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label htmlFor="start" className="block text-sm font-medium text-gray-700">Start Time</label>
                    <input
                      type="time"
                      name="start"
                      value={newShift.start}
                      onChange={handleInputChange}
                      className="mt-1 block w-full px-3 py-2 rounded-lg border border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                      required
                    />
                </div>
                <div>
                    <label htmlFor="end" className="block text-sm font-medium text-gray-700">End Time</label>
                    <input
                      type="time"
                      name="end"
                      value={newShift.end}
                      onChange={handleInputChange}
                      className="mt-1 block w-full px-3 py-2 rounded-lg border border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                      required
                    />
                </div>
            </div>
            
            <div>
              <label htmlFor="grace" className="block text-sm font-medium text-gray-700">Grace Period (Optional)</label>
              <input
                type="text"
                name="grace"
                value={newShift.grace}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 rounded-lg border border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                placeholder="e.g., 15 min"
              />
            </div>
            <button
              type="submit"
              className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-xl shadow-lg text-white bg-teal-600 hover:bg-teal-700 transition duration-200 transform hover:scale-[1.01] focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 font-bold"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Add New Shift
            </button>
          </form>
        </motion.div>

        {/* Shift List and Assignment Section */}
        <motion.div
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.2 }}
          className="lg:col-span-2 bg-white p-6 rounded-2xl border border-gray-100 shadow-xl h-full flex flex-col"
        >
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Poppins, sans-serif' }}>Defined Shifts</h3>
            <button className="text-teal-600 hover:text-teal-800 font-semibold text-sm flex items-center p-2 rounded-lg transition duration-150">
                Assign Shifts
                <ArrowRightIcon className="h-4 w-4 ml-2" />
            </button>
          </div>
          
          {/* Responsive Table Container: forces horizontal scroll on small devices */}
          <div className="overflow-x-auto flex-1">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Shift Name</th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timing</th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grace Period</th>
                  <th className="relative px-3 sm:px-6 py-3"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <AnimatePresence initial={false}>
                  {shifts.map((shift) => (
                    <motion.tr
                      key={shift.id}
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="hover:bg-teal-50/50"
                    >
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{shift.name}</td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        <div className="flex items-center space-x-2">
                          <ClockIcon className="h-4 w-4 text-gray-400" />
                          <span>{`${shift.start} - ${shift.end}`}</span>
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-600">{shift.grace || 'None'}</td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => handleDeleteShift(shift.id)}
                            className="text-red-600 hover:text-red-800 p-1 rounded-full hover:bg-red-50 transition duration-150"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                          <button
                            className="text-indigo-600 hover:text-indigo-800 p-1 rounded-full hover:bg-indigo-50 transition duration-150"
                          >
                            <PencilIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
          {shifts.length === 0 && (
              <p className="text-center text-gray-500 py-12">No shifts defined yet. Use the form to create one!</p>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default RotationalShift;